/**
 * Farmgrid Account Card (Organization detail page)
 *
 * The Farmgrid account is a per-organization data feature stored on the vertical
 * (custom-attribute) model: exactly one per org, provisioned at org creation with
 * accountStatus = inactive. It is NOT a multi-account entity — the only thing that
 * changes is its status (active | inactive | unpaid).
 *
 * Endpoints (real backend, feat/org-vertical-model-for-accounts):
 * - GET   /mint/organizations/:orgNumber/farmgrid-account  (getOrganizationFarmgridAccount)
 * - PATCH /mint/organizations/:orgNumber/farmgrid-account  (updateOrganizationFarmgridAccount)
 *
 * Changing the status cascades to the org's memberships server-side
 * (active → all active, inactive → all inactive, unpaid → no change); the response
 * returns membershipsUpdated.
 */

import * as React from 'react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { ChevronDown, ChevronUp, Sprout, Loader2 } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// Farmgrid account status enum (from the backend contract).
const ACCOUNT_STATUSES: { value: string; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'unpaid', label: 'Unpaid' },
]

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

export interface FarmgridAccountCardProps {
  organizationId: string
  isArchived: boolean // Organization archive status
}

export function FarmgridAccountCard(props: FarmgridAccountCardProps) {
  const { organizationId, isArchived } = props
  const { toast } = useToast()
  const orgNumber = Number(organizationId)

  const { data, isLoading, isError, error, refetch } = useApiQuery(
    'getOrganizationFarmgridAccount',
    { orgNumber },
    { retry: 0 },
  )

  const updateAccount = useApiMutation('updateOrganizationFarmgridAccount', 'PATCH', {
    invalidates: ['getOrganizationFarmgridAccount'],
  })

  const currentStatus: string | undefined =
    (data as any)?.content?.organization?.farmgridAccount?.accountStatus

  // A 404 (FARMGRID_ACCOUNT_NOT_FOUND) means the feature isn't provisioned for this org.
  const notProvisioned =
    isError && /not\s*found|not\s*provisioned/i.test((error as Error)?.message ?? '')

  const [isOpen, setIsOpen] = React.useState(true)
  const [selected, setSelected] = React.useState<string>('')

  // Seed the selector from the fetched status once it arrives.
  React.useEffect(() => {
    if (currentStatus && !selected) setSelected(currentStatus)
  }, [currentStatus, selected])

  const canManage = !isArchived && !!currentStatus
  const changed = !!selected && !!currentStatus && selected !== currentStatus
  const busy = updateAccount.isPending

  const handleSave = async () => {
    if (!changed) return
    try {
      const res: any = await updateAccount.mutateAsync({
        orgNumber,
        organization: { farmgridAccount: { accountStatus: selected } },
      })
      const membershipsUpdated = res?.content?.membershipsUpdated
      toast({
        title: 'Farmgrid account updated',
        description:
          typeof membershipsUpdated === 'number'
            ? `Status set to ${cap(selected)}. ${membershipsUpdated} membership(s) updated.`
            : `Status set to ${cap(selected)}.`,
        type: 'success',
      })
      refetch()
    } catch (err) {
      log.error('Failed to update Farmgrid account', err)
      toast({
        title: 'Could not update Farmgrid account',
        description: (err as Error)?.message || 'Please try again.',
        type: 'error',
      })
    }
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border shadow-sm">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-auto">
                  {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </Button>
              </CollapsibleTrigger>
              <CardTitle className="flex items-center gap-2">
                <Sprout className="h-4 w-4" />
                Farmgrid Account
              </CardTitle>
            </div>
            {currentStatus && (
              <Badge variant={currentStatus === 'active' ? 'default' : 'secondary'}>
                {cap(currentStatus)}
              </Badge>
            )}
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : notProvisioned ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No Farmgrid account is provisioned for this organization.
              </p>
            ) : isError ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <p className="text-sm text-destructive">Failed to load the Farmgrid account.</p>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Try Again
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-2 max-w-xs">
                  <Label>Account Status</Label>
                  <Select value={selected} onValueChange={setSelected} disabled={!canManage || busy}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSave} disabled={!canManage || !changed || busy}>
                  {busy ? 'Saving...' : 'Save'}
                </Button>
                {isArchived && (
                  <p className="w-full text-xs text-muted-foreground">
                    Organization is archived — status changes are disabled.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
